import './index.css';
import { jsx, jsxs } from 'react/jsx-runtime';
import { forwardRef, useId, useState, useMemo, useRef, useLayoutEffect } from 'react';
import classnames from 'classnames';
import { DropBase } from '@hh.ru/magritte-internal-drop-base';
import { Action } from '@hh.ru/magritte-ui-action';
import { useBreakpoint } from '@hh.ru/magritte-ui-breakpoint';
import { STRETCHED_WRAPPER_CLASS } from '@hh.ru/magritte-ui-button';
import { CrossOutlinedSize24 } from '@hh.ru/magritte-ui-icon/icon';
import { Layer } from '@hh.ru/magritte-ui-layer';
import { TooltipArrow } from './TooltipArrow.js';
import { Text } from '@hh.ru/magritte-ui-typography';
import { BaseText } from '@hh.ru/magritte-ui-typography/BaseText';

var styles = {"tooltip":"magritte-tooltip___Hxyh2_9-0-7","tooltip-with-buttons":"magritte-tooltip-with-buttons___pQzdt_9-0-7","tooltipWithButtons":"magritte-tooltip-with-buttons___pQzdt_9-0-7","tooltip-with-close":"magritte-tooltip-with-close___2iNPt_9-0-7","tooltipWithClose":"magritte-tooltip-with-close___2iNPt_9-0-7","tooltip-small":"magritte-tooltip-small___W1eTg_9-0-7","tooltipSmall":"magritte-tooltip-small___W1eTg_9-0-7","tooltip-title":"magritte-tooltip-title___s-ekd_9-0-7","tooltipTitle":"magritte-tooltip-title___s-ekd_9-0-7","tooltip-close":"magritte-tooltip-close___B1qWh_9-0-7","tooltipClose":"magritte-tooltip-close___B1qWh_9-0-7","tooltip-enter":"magritte-tooltip-enter___cnped_9-0-7","tooltipEnter":"magritte-tooltip-enter___cnped_9-0-7","css-variables-element":"magritte-css-variables-element___yRnOI_9-0-7","cssVariablesElement":"magritte-css-variables-element___yRnOI_9-0-7","tooltip-enter-active":"magritte-tooltip-enter-active___M8qsm_9-0-7","tooltipEnterActive":"magritte-tooltip-enter-active___M8qsm_9-0-7","tooltip-exit":"magritte-tooltip-exit___IXkNp_9-0-7","tooltipExit":"magritte-tooltip-exit___IXkNp_9-0-7","tooltip-exit-active":"magritte-tooltip-exit-active___QllYr_9-0-7","tooltipExitActive":"magritte-tooltip-exit-active___QllYr_9-0-7","tooltip-buttons":"magritte-tooltip-buttons___kNN5e_9-0-7","tooltipButtons":"magritte-tooltip-buttons___kNN5e_9-0-7","tooltip-arrow":"magritte-tooltip-arrow___7OOYT_9-0-7","tooltipArrow":"magritte-tooltip-arrow___7OOYT_9-0-7"};

const CSS_CLASSES = {
    enter: styles.tooltipEnter,
    enterActive: styles.tooltipEnterActive,
    exit: styles.tooltipExit,
    exitActive: styles.tooltipExitActive,
};
const PLACEMENTS_LIST = [
    'left-center',
    'top-center',
    'right-center',
    'bottom-center',
    'left-top',
    'left-bottom',
    'top-left',
    'top-right',
    'right-bottom',
    'right-top',
    'bottom-left',
    'bottom-right',
];
// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => { };
const Tooltip = forwardRef(({ visible, activatorRef, onClose = noop, maxWidth = 400, title, children, showClose, closeByClickOutside = true, placement, buttons, forcePlacement, 'aria-label-close': ariaLabelClose, role = 'tooltip', size = 'medium', ignoreIntersections = true, lockPlacement = true, guardDistanceToActivator: _guardDistanceToActivator, ...props }, ref) => {
    const { isMobile } = useBreakpoint();
    const id = useId();
    const ariaLabledBy = `tooltip-aria-label-${id}`;
    const ariaDescribedBy = `tooltip-aria-description-${id}`;
    const [animationTimeout, setAnimationTimeout] = useState(200);
    const [arrowGuardDistance, setArrowGuardDistance] = useState({
        left: { top: 0, bottom: 0, center: 0 },
        right: { top: 0, bottom: 0, center: 0 },
        top: { left: 0, center: 0, right: 0 },
        bottom: { left: 0, center: 0, right: 0 },
    });
    const placementsList = useMemo(() => {
        const placementsArray = Array.isArray(placement) ? placement : [placement];
        return forcePlacement
            ? placementsArray
            : [...placementsArray, ...PLACEMENTS_LIST.filter((place) => !placement.includes(place))];
    }, [placement, forcePlacement]);
    const tooltipElementRef = useRef(null);
    const ariaRole = role === 'tooltip' && buttons ? 'alertdialog' : role;
    const isSmall = size === 'small';
    const guardDistanceToActivator = _guardDistanceToActivator ?? (isSmall ? 11 : 20);
    useLayoutEffect(() => {
        if (!tooltipElementRef.current) {
            return;
        }
        const style = window.getComputedStyle(tooltipElementRef.current);
        const animationTimeout = parseInt(style.getPropertyValue('--magritte-tooltip-animation-duration'), 10);
        if (Number.isInteger(animationTimeout)) {
            setAnimationTimeout(animationTimeout);
        }
        const borderRadius = parseInt(style.getPropertyValue('--magritte-tooltip-border-radius'), 10);
        const arrowWidth = parseInt(style.getPropertyValue('--magritte-tooltip-arrow-width'), 10);
        if (Number.isInteger(borderRadius) && Number.isInteger(arrowWidth)) {
            const arrowGap = borderRadius + arrowWidth / 2;
            setArrowGuardDistance({
                left: {
                    top: arrowGap,
                    bottom: arrowGap,
                    center: 0,
                },
                right: {
                    top: arrowGap,
                    bottom: arrowGap,
                    center: 0,
                },
                top: {
                    left: arrowGap,
                    center: 0,
                    right: arrowGap,
                },
                bottom: {
                    left: arrowGap,
                    center: 0,
                    right: arrowGap,
                },
            });
        }
    }, [buttons, setAnimationTimeout, isSmall, visible]);
    if (isMobile) {
        return null;
    }
    return (jsx(Layer, { layer: "tooltip", children: jsx(DropBase, { ...props, placement: placementsList, visible: visible, activatorRef: activatorRef, arrowGuardDistance: arrowGuardDistance, closeByClickOutside: closeByClickOutside, onClose: onClose, autoFocusWhenOpened: ariaRole !== 'tooltip', guardDistanceToActivator: guardDistanceToActivator, guardDistanceToViewport: 0, allowShrinkHeightToFitIntoViewport: false, animationClassNames: CSS_CLASSES, animationTimeout: animationTimeout, ignoreIntersections: ignoreIntersections, role: ariaRole, "aria-labelledby": title && role !== 'tooltip' ? ariaLabledBy : undefined, "aria-describedby": role !== 'tooltip' ? ariaDescribedBy : undefined, ref: ref, lockPlacement: lockPlacement, children: () => (jsxs("div", { "data-qa": "tooltip", ref: tooltipElementRef, className: classnames(styles.tooltip, {
                    [styles.tooltipWithButtons]: !isSmall && !!buttons,
                    [styles.tooltipWithClose]: !isSmall && showClose,
                    [styles.tooltipSmall]: isSmall,
                }), style: {
                    maxWidth: `${maxWidth}px`,
                }, children: [title && !isSmall && (jsx("div", { className: styles.tooltipTitle, children: jsx(BaseText, { id: ariaLabledBy, "data-qa": "tooltip-title", Element: "div", style: "primary", typography: "title-5-semibold", children: title }) })), jsx(Text, { typography: isSmall ? 'paragraph-3-regular' : 'paragraph-2-regular', id: ariaDescribedBy, children: children }), buttons && !isSmall && (jsx("div", { className: classnames(styles.tooltipButtons, STRETCHED_WRAPPER_CLASS), children: typeof buttons === 'function' ? buttons(onClose) : buttons })), showClose && !isSmall && (jsx("div", { className: styles.tooltipClose, children: jsx(Action, { "data-qa": "tooltip-close-action", "aria-label": ariaLabelClose, icon: CrossOutlinedSize24, mode: "secondary", onClick: () => onClose?.(), style: "contrast" }) })), jsx(TooltipArrow, { className: styles.tooltipArrow, size: size })] })) }) }));
});
Tooltip.displayName = 'Tooltip';

export { Tooltip };
//# sourceMappingURL=Tooltip.js.map
